<template>
  <div>
    <gmap-map
      ref="map"
      :center="center"
      :zoom="12"
      class="map-container"
    >
      <gmap-cluster>
        <gmap-marker
          v-for="(m, index) in markers"
          :key="'marker' + index"
          :position="{lat: m.lat, lng: m.lng}"
          @click="toggleInfoWindow(m.lat, m.lng, m.info)"
        />
      </gmap-cluster>
      <template v-if="paths.length > 0">
        <gmap-polyline
          v-for="(p, p_index) in paths"
          :key="'path' + p_index"
          ref="polyline"
          :path="p.polyline"
          :editable="false"
          :options="{ strokeColor: '#FF0000'}"
          :clickable="true"
          @click="toggleInfoWindow($event.latLng.lat(), $event.latLng.lng(), p.info)"
        />
      </template>
      <gmap-info-window
        :options="infoOptions"
        :position="infoWindowPos"
        :opened="infoWinOpen"
        @closeclick="infoWinOpen=false"
      >
        <div class="card">
          <template v-for="key in Object.keys(info)">
            <div :key="key">
              <b>{{ key }}</b>: {{ info[key] }}<br>
            </div>
          </template>
        </div>
      </gmap-info-window>
    </gmap-map>
    <div v-if="markers.length > 0 && maxMarkerNum < dataLength">
      * Only first {{ maxMarkerNum }} markers are displayed to have a better browsing experience.
    </div>
    <div v-if="paths.length > 0 && maxPolylineNum < dataLength">
      * Only first {{ maxPolylineNum }} polylines are displayed to have a better browsing experience.
    </div>
  </div>
</template>

<script>
import { gmapApi } from 'vue2-google-maps'
import GmapCluster from 'vue2-google-maps/dist/components/cluster'

export default {
  name: 'GoogleMap',

  components: {
    GmapCluster
  },

  props: {
    data: Array
  },

  data () {
    let paths = []
    let markers = []

    const maxMarkerNum = 2000
    const maxPolylineNum = 200

    const latTitles = []
    const lngTitles = []

    const length = this.data.length

    Object.keys(this.data[0]).forEach((title) => {
      if (title.includes('LAT')) {
        let i = 0
        this.data.forEach((row) => {
          if (!row[title] || (typeof row[title] === 'number' && row[title] <= 90 && row[title] >= -90)) {
            i += 1
          }
        })
        if (i >= length * 0.5) {
          latTitles.push(title)
        }
      }
      if (title.includes('LONG') || title.includes('LNG')) {
        let i = 0
        this.data.forEach((row) => {
          if (!row[title] || (typeof row[title] === 'number' && row[title] <= 180 && row[title] >= -180)) {
            i += 1
          }
        })
        if (i >= length * 0.5) {
          lngTitles.push(title)
        }
      }
    })

    if (latTitles.length === 1) {
      markers = this.data.filter(row => row[latTitles[0]] && row[lngTitles[0]])
        .map((row) => {
          return Object.freeze({
            lat: row[latTitles[0]], lng: row[lngTitles[0]], info: row
          })
        }).slice(0, maxMarkerNum)
    } else if (latTitles.length === 2) {
      paths = this.data.filter(row => row[latTitles[0]] && row[lngTitles[0]] && row[latTitles[1]] && row[lngTitles[1]]).map(row => {
        return {
          polyline: [
            Object.freeze({ lat: row[latTitles[0]], lng: row[lngTitles[0]] }),
            Object.freeze({ lat: row[latTitles[1]], lng: row[lngTitles[1]] })
          ],
          info: row
        }
      }).slice(0, maxPolylineNum)
    }

    return {
      center: { lat: 0, lng: 0 },
      paths: paths,
      markers: markers,
      infoOptions: {
        pixelOffset: {
          width: 0,
          height: -35
        }
      },
      info: {},
      infoWindowPos: {
        lat: 0,
        lng: 0
      },
      infoWinOpen: false,
      dataLength: length,
      maxMarkerNum: maxMarkerNum,
      maxPolylineNum: maxPolylineNum,
      latTitles: latTitles,
      lngTitles: lngTitles
    }
  },

  computed: {
    google: gmapApi
  },

  mounted () {
    this.$refs.map.$mapPromise.then((map) => {
      const bounds = this.createBounds(this.data)
      map.fitBounds(bounds)
      map.panToBounds(bounds)
    })
  },

  methods: {
    /**
     * Toggle info window on google map
     * @param {Number} lat Latitude
     * @param {Number} lng Longitude
     * @param {Object} info Data used for info window display
     */
    toggleInfoWindow (lat, lng, info) {
      this.infoWindowPos = { lat: lat, lng: lng }
      this.info = info
      this.infoWinOpen = true
    },

    /** Create Bounds for Markers
     * @param {Array} data original data to compute
     * @return {Array} computed bounds for markers
     */
    createBounds (data) {
      const bounds = new this.google.maps.LatLngBounds()

      data.map((each) => {
        return bounds.extend(new this.google.maps.LatLng(
          each[this.latTitles[0]], each[this.lngTitles[0]]
        ))
      })

      return bounds
    }
  }
}
</script>

<style scoped>
  .map-container {
    width:100%;
    height: 350px;
  }
</style>
